import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'

import { useGoogleLogin } from 'api/auth/googleLogin'
import { useTwitterLogin } from 'api/auth/twitterLogin'
import { Button } from 'components/Button/Button'
import CheckboxField from 'components/Form/CheckboxField/CheckboxField'
import InputField from 'components/Form/InputField/InputField'
import { addToast } from 'components/Toast/Toast'
import { useProfileContext } from 'context/profileContext'
import { Icons } from 'helpers/icons'
import { type UserProfile } from 'models'
import PrivacyPolicyModal from 'pages/landingPage/modals/PrivacyPolicyModal'
import TermsModal from 'pages/landingPage/modals/TermsModal'
import { registerUser } from 'services/endpoints/auth'
import { useSetUserProfile } from 'store'
import { type quickRegistration } from 'types/types'

export const registrationOpen = false

type RegistrationRole = 'fan' | 'creator'

type RegistrationStep =
    | 'role'
    | 'account'
    | 'social'
    | 'success'

type RegistrationData = quickRegistration & {
    instagram: string
    twitter: string
    youtube: string
    tiktok: string
}

interface RegisterProps {
  onInputFocus?: (
    e: React.SyntheticEvent<HTMLElement>
  ) => void

  fromSubscribeModal?: boolean
  onBackToLogin?: () => void
}

export const Register = ({
  onInputFocus,
  fromSubscribeModal = false,
  onBackToLogin
}: RegisterProps) => {
    const urlParams = new URLSearchParams(location.search)
    const referral_code = urlParams.get('ref')

    const openTwitterLoginMutation = useTwitterLogin()
    const getGoogleRedirectURL = useGoogleLogin()
    const [activeLegalModal, setActiveLegalModal] = useState<
        'terms' | 'privacy' | null
    >(null)
    const [registrationData, setRegistrationData] =
        useState<RegistrationData>({
            name: '',
            username: '',
            email: '',
            password: '',
            password_confirmation: '',
            is_model: 0,
            referral_code: referral_code ?? '',
            instagram: '',
            twitter: '',
            youtube: '',
            tiktok: ''
        })

const [registrationStep, setRegistrationStep] =
  useState<RegistrationStep>(
    fromSubscribeModal
      ? 'account'
      : registrationOpen
        ? 'account'
        : 'role'
  )
    const [selectedRole, setSelectedRole] =
        useState<RegistrationRole>('fan')

    const [termsAccepted, setTermsAccepted] =
        useState<boolean>(false)

    const [registrationLocked, setRegistrationLocked] =
        useState<boolean>(false)

    const [passwordInputType, setPasswordInputType] =
        useState<boolean>(true)

    const [isMobileView, setIsMobileView] =
        useState(false)

    const [isSubmitting, setIsSubmitting] =
        useState(false)

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 768)
        }

        handleResize()

        window.addEventListener(
            'resize',
            handleResize
        )

        return () =>
            window.removeEventListener(
                'resize',
                handleResize
            )
    }, [])

    const navigate = useNavigate()
    const { t } = useTranslation()

    const queryClient = useQueryClient()

    const { profileMarker } = useProfileContext()

    const updateRegistrationData = (
        field: keyof RegistrationData,
        val: string
    ): void => {
        if (field === 'password') {
            setRegistrationData(prev => ({
                ...prev,
                password: val,
                password_confirmation: val
            }))

            return
        }

        setRegistrationData(prev => ({
            ...prev,
            [field]: val
        }))
    }

    const selectRole = (
        role: RegistrationRole
    ) => {
        setSelectedRole(role)

        setRegistrationData(prev => ({
            ...prev,
            is_model:
                role === 'creator'
                    ? 1
                    : 0
        }))

        setRegistrationStep('account')
    }

    const setLoggedInUser = (
        user: UserProfile
    ) => {
        useSetUserProfile(user)

        queryClient.setQueryData(
            ['user-profile'],
            user
        )
    }

    const submitErrorFn = (err: any) => {
        let errorWithMsg = false

        if (err?.response?.status === 422) {
            if (
                err.response.data?.errors?.email?.[0] ===
                'The email has already been taken.'
            ) {
                addToast(
                    'error',
                    t('error:thatEmailIsTaken')
                )

                errorWithMsg = true
            }

            if (
                err.response.data?.errors?.name?.[0] ===
                'The name has already been taken.'
            ) {
                addToast(
                    'error',
                    t(
                        'error:thatDisplayNameIsTaken'
                    )
                )

                errorWithMsg = true
            }

            if (
                err.response.data?.errors
                    ?.username?.[0] ===
                'The username has already been taken.'
            ) {
                addToast(
                    'error',
                    t('error:thatUsernameIsTaken')
                )

                errorWithMsg = true
            }

            if (!errorWithMsg) {
                addToast(
                    'error',
                    t('error:errorInvalidData')
                )
            }
        } else {
            addToast(
                'error',
                t('error:registrationError')
            )
        }
    }

    const missingData = (): boolean => {
        const {
            username,
            email,
            password
        } = registrationData

        return (
            username.length === 0 ||
            email.length === 0 ||
            password.length === 0 ||
            !termsAccepted
        )
    }

    const submitForm = async () => {
        if (missingData()) {
            addToast(
                'error',
                t(
                    'error:errorInvalidDataRegister'
                )
            )

            return
        }

        if (isSubmitting) {
            return
        }

        setIsSubmitting(true)

        try {
            const userProfile = await registerUser(registrationData)

if (userProfile === null) {
    // Dismiss the mobile keyboard.
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
    }

    // Display the success screen.
    setRegistrationStep('success')

    // Scroll the containing modal/form back to the top.
    requestAnimationFrame(() => {
        const container = document.querySelector(
            '.auth-register'
        )

        const scrollParent = container
            ? (() => {
                let el = container.parentElement

                while (el) {
                    if (el.scrollHeight > el.clientHeight &&
                        getComputedStyle(el).overflowY !== 'visible') {
                        return el
                    }

                    el = el.parentElement
                }

                return null
            })()
            : null

        scrollParent?.scrollTo({
            top: 0,
            behavior: 'auto'
        })
    })

    return
}

            setLoggedInUser(userProfile)

            if (
                typeof profileMarker.profileId ===
                    'number' &&
                profileMarker.profileId > 0
            ) {
                window.location.reload()
                return
            }

            if (
                userProfile.role ===
                    'model' &&
                userProfile.username
            ) {
                navigate(
                    `/${userProfile.username}/`,
                    {
                        replace: true
                    }
                )
            } else {
                navigate('/', {
                    replace: true
                })
            }
        } catch (err: any) {
            submitErrorFn(err)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleAccountContinue = () => {
        if (missingData()) {
            addToast(
                'error',
                t(
                    'error:errorInvalidDataRegister'
                )
            )

            return
        }

        if (
            !registrationOpen &&
            selectedRole === 'creator'
        ) {
            setRegistrationStep('social')
            return
        }

        void submitForm()
    }

    const handleBack = () => {
        if (
            !registrationOpen &&
            registrationStep === 'social'
        ) {
            setRegistrationStep('account')
            return
        }

        if (
            !registrationOpen &&
            registrationStep === 'account'
        ) {
            setRegistrationStep('role')
            return
        }

        window.dispatchEvent(
            new CustomEvent(
                'mobile-auth-back'
            )
        )
    }

    const googleLogin = async () => {
        if (
            getGoogleRedirectURL.isLoading
        ) {
            return
        }

        try {
            const url =
                await getGoogleRedirectURL.mutateAsync()

            window.location.href = url
        } catch (_) {
            addToast(
                'error',
                t(
                    'error:errorSomethingWentWrong'
                )
            )
        }
    }

    const twitterLogin = async () => {
        if (
            openTwitterLoginMutation.isLoading
        ) {
            return
        }

        openTwitterLoginMutation.mutate()
    }

if (
    !registrationOpen &&
    registrationStep === 'role'
) {
    return (
        <div className='auth-register'>
            <h1 className='auth-register__title'>
                Pre Register
            </h1>

            <div className='auth-register__form'>
                <div
                    className='auth-register__role-selection'
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <p
                        style={{
                            textAlign: 'center',
                            fontWeight: 500,
                            fontSize: '1.4rem',
                            paddingBottom: '2rem',
                            color: '#000'
                        }}
                    >
                        How will you use AllFans?
                    </p>

                    <button
                        type='button'
                        className='button button--blue submit-btn'
                        onClick={() => selectRole('fan')}
                    >
                        Fan
                    </button>

                    <button
                        type='button'
                        className='button button--blue submit-btn'
                        onClick={() => selectRole('creator')}
                    >
                        Creator
                    </button>

                    {isMobileView && (
                        <div
                            style={{
                                width: '100%',
                                marginTop: '0.5rem',
                                paddingTop: '1.5rem',
                                borderTop: '1px solid rgba(105, 107, 115, 0.2)'
                            }}
                        >
<button
  type='button'
    className={`auth-register__back-btn auth-register__back-btn--role ${
        fromSubscribeModal
            ? 'auth-register__back-btn--subscribe-modal'
            : ''
    }`}
  onClick={() => {
    if (fromSubscribeModal) {
      onBackToLogin?.()
      return
    }

    handleBack()
  }}
>
  {fromSubscribeModal ? 'Back to Login' : 'Back'}
</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

if (
    !registrationOpen &&
    registrationStep === 'social'
) {
    return (
        <div className='auth-register'>
            <h1 className='auth-register__title'>
                Add Your Social Media
            </h1>

            <div className='auth-register__form'>
                <div className='form-group field-social'>
                    <input
                        type='text'
                        className='form-field'
                        placeholder='Instagram'
                        value={registrationData.instagram}
                        onFocus={onInputFocus as any}
                        onChange={e => {
                            updateRegistrationData(
                                'instagram',
                                e.target.value
                            )
                        }}
                    />

                    <div className='social-icon social-icon__instagram'>
                        <svg
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                d='M7.16797 3.125C4.94873 3.125 3.125 4.94629 3.125 7.16797V12.832C3.125 15.0513 4.94629 16.875 7.16797 16.875H12.832C15.0513 16.875 16.875 15.0537 16.875 12.832V7.16797C16.875 4.94873 15.0537 3.125 12.832 3.125H7.16797ZM7.16797 4.375H12.832C14.3774 4.375 15.625 5.62256 15.625 7.16797V12.832C15.625 14.3774 14.3774 15.625 12.832 15.625H7.16797C5.62256 15.625 4.375 14.3774 4.375 12.832V7.16797C4.375 5.62256 5.62256 4.375 7.16797 4.375ZM13.6914 5.74219C13.3765 5.74219 13.125 5.99365 13.125 6.30859C13.125 6.62353 13.3765 6.875 13.6914 6.875C14.0063 6.875 14.2578 6.62353 14.2578 6.30859C14.2578 5.99365 14.0063 5.74219 13.6914 5.74219ZM10 6.25C7.93701 6.25 6.25 7.93701 6.25 10C6.25 12.063 7.93701 13.75 10 13.75C12.063 13.75 13.75 12.063 13.75 10C13.75 7.93701 12.063 6.25 10 6.25ZM10 7.5C11.3892 7.5 12.5 8.61084 12.5 10C12.5 11.3892 11.3892 12.5 10 12.5C8.61084 12.5 7.5 11.3892 7.5 10C7.5 8.61084 8.61084 7.5 10 7.5Z'
                                fill='#16161C'
                            />
                        </svg>
                    </div>
                </div>

                <div className='form-group field-social'>
                    <input
                        type='text'
                        className='form-field'
                        placeholder='Twitter / X'
                        value={registrationData.twitter}
                        onFocus={onInputFocus as any}
                        onChange={e => {
                            updateRegistrationData(
                                'twitter',
                                e.target.value
                            )
                        }}
                    />

                    <div className='social-icon social-icon__twitter'>
                        <svg
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                d='M17.58 17.3327L11.7167 8.78468L11.7267 8.79268L17.0133 2.66602H15.2467L10.94 7.65268L7.52 2.66602H2.88667L8.36067 10.6467L8.36 10.646L2.58667 17.3327H4.35334L9.14134 11.7847L12.9467 17.3327H17.58ZM6.82 3.99935L15.0467 15.9993H13.6467L5.41334 3.99935H6.82Z'
                                fill='#16161C'
                            />
                        </svg>
                    </div>
                </div>

                <div className='form-group field-social'>
                    <input
                        type='text'
                        className='form-field'
                        placeholder='YouTube'
                        value={registrationData.youtube}
                        onFocus={onInputFocus as any}
                        onChange={e => {
                            updateRegistrationData(
                                'youtube',
                                e.target.value
                            )
                        }}
                    />

                    <div className='social-icon social-icon__youtube'>
                        <svg
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                d='M9.99998 2.66602C7.20931 2.66602 3.58722 3.36523 3.58722 3.36523L3.5781 3.37565C2.30709 3.57893 1.33331 4.6711 1.33331 5.99935V9.99935V10.0007V13.9993V14.0007C1.33455 14.635 1.56189 15.2481 1.97451 15.73C2.38713 16.2118 2.958 16.5308 3.58461 16.6296L3.58722 16.6335C3.58722 16.6335 7.20931 17.334 9.99998 17.334C12.7906 17.334 16.4127 16.6335 16.4127 16.6335L16.414 16.6322C17.0413 16.5336 17.6129 16.2144 18.0258 15.732C18.4387 15.2496 18.666 14.6356 18.6666 14.0007V13.9993V10.0007V9.99935V5.99935C18.6657 5.36477 18.4385 4.75132 18.0259 4.26922C17.6132 3.78712 17.0422 3.46798 16.4153 3.36914L16.4127 3.36523C16.4127 3.36523 12.7906 2.66602 9.99998 2.66602ZM7.99998 6.93164L13.3333 9.99935L7.99998 13.0671V6.93164Z'
                                fill='#16161C'
                            />
                        </svg>
                    </div>
                </div>

                <div className='form-group field-social'>
                    <input
                        type='text'
                        className='form-field'
                        placeholder='TikTok'
                        value={registrationData.tiktok}
                        onFocus={onInputFocus as any}
                        onChange={e => {
                            updateRegistrationData(
                                'tiktok',
                                e.target.value
                            )
                        }}
                    />

                    <div className='social-icon social-icon__tiktok'>
                        <svg
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                d='M5 2.5C3.62919 2.5 2.5 3.62919 2.5 5V15C2.5 16.3708 3.62919 17.5 5 17.5H15C16.3708 17.5 17.5 16.3708 17.5 15V5C17.5 3.62919 16.3708 2.5 15 2.5H5ZM5 4.16667H15C15.4709 4.16667 15.8333 4.52914 15.8333 5V15C15.8333 15.4709 15.4709 15.8333 15 15.8333H5C4.52914 15.8333 4.16667 15.4709 4.16667 15V5C4.16667 4.52914 4.52914 4.16667 5 4.16667ZM10 5.83333V11.6667C10 12.1375 9.63752 12.5 9.16667 12.5C8.69581 12.5 8.33333 12.1375 8.33333 11.6667C8.33333 11.1958 8.69581 10.8333 9.16667 10.8333V9.16667C7.79586 9.16667 6.66667 10.2959 6.66667 11.6667C6.66667 13.0375 7.79586 14.1667 9.16667 14.1667C10.5375 14.1667 11.6667 13.0375 11.6667 11.6667V8.52702C12.1801 8.89278 12.7159 9.16667 13.3333 9.16667V7.5C13.2939 7.5 12.7189 7.31745 12.3177 6.96777C11.9165 6.6181 11.6667 6.17916 11.6667 5.83333H10Z'
                                fill='#16161C'
                            />
                        </svg>
                    </div>
                </div>

                <div className='auth-register__action auth-register__action--split'>
                    <button
                        type='button'
                        className='auth-register__back-btn'
                        onClick={() => setRegistrationStep('account')}
                    >
                        Back
                    </button>

                    <Button
                        text={isSubmitting ? 'Submitting...' : 'Pre Register'}
                        color='blue'
                        customClass='submit-btn auth-register__submit-btn'
                        clickFn={() => {
                            void submitForm()
                        }}
                        disabled={isSubmitting}
                    />
                </div>
            </div>
        </div>
    )
}

if (registrationStep === 'success') {
    return (
        <div className='auth-register'>
            <h1 className='auth-register__title'>
                You're Pre-Registered!
            </h1>

            <div className='auth-register__form'>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: '1.5rem',
                        padding: '1rem 1rem 2rem 1rem'
                    }}
                >
                    <div
                        style={{
                            width: '4rem',
                            height: '4rem',
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            color: '#2196F3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            fontWeight: 600
                        }}
                    >
                        ✓
                    </div>

                    <h2
                        style={{
                            fontSize: '1.6rem',
                            fontWeight: 600,
                            color: '#10143F',
                            margin: 0
                        }}
                    >
                        Your Username Is Reserved!
                    </h2>

                    <p
                        style={{
                            fontSize: '1.4rem',
                            color: '#696B73',
                            fontWeight: 500,
                            margin: 0,
                            textAlign: 'center'
                        }}
                    >
                        <strong>
                            @{registrationData.username}
                        </strong>
                    </p>

                    <p
                        style={{
                            fontSize: '1.4rem',
                            lineHeight: 1.6,
                            color: '#696B73',
                            textAlign: 'center',
                            maxWidth: '35rem'
                        }}
                    >
                        Thank you for pre-registering with AllFans!
                        Your username has been reserved and
                        we'll send you an email when the
                        platform officially launches.
                    </p>

                </div>
            </div>
        </div>
    )
}



    return (
        <div
  className={`auth-register ${
    fromSubscribeModal ? 'auth-register--subscribe-modal' : ''
  }`}
>
            <h1 className='auth-register__title'>
                {registrationOpen
                    ? t('signup')
                    : selectedRole ===
                        'creator'
                      ? 'Creator Pre Registration'
                      : 'Fan Pre Registration'}
            </h1>

            <div className='auth-register__form'>
                {registrationOpen && !isMobileView && (
                    <>
                        <Button
                            text={t(
                                'continueTwitter'
                            )}
                            color='white'
                            customClass='auth-login__twitter'
                            hasCircleIcon={true}
                            circleIcon={
                                Icons.twitter
                            }
                            circleIconBg='twitter'
                            clickFn={twitterLogin}
                        />

                        <Button
                            text={t(
                                'continueGoogle'
                            )}
                            color='white'
                            customClass='auth-login__google'
                            hasCircleIcon={true}
                            circleIcon={
                                Icons.google
                            }
                            circleIconBg='google'
                            clickFn={async () =>
                                await googleLogin()
                            }
                        />

                        <div className='divider'>
                            <span>Or</span>
                        </div>
                    </>
                )}

                <div onFocusCapture={onInputFocus}>
                    <InputField
                        id='registration-name'
                        type='text'
                        value={
                            registrationData.name
                        }
                        label={t(
                            'displayName'
                        )}
                        changeFn={(
                            val: string
                        ) => {
                            updateRegistrationData(
                                'name',
                                val
                            )
                        }}
                        validate='letters/numbers/spaces'
                        validateEmptyOnBlur={false}
                        validationCheck={(
                            val: boolean
                        ) => {
                            setRegistrationLocked(
                                val
                            )
                        }}
                        validationErrorMessage={t(
                            'error:errorDisplayName'
                        )}
                        validationCharactersNumber={
                            4
                        }
                        additionalProps={{
                            placeholder:
                                t(
                                    'displayName'
                                )
                        }}
                    />
                </div>

                <div onFocusCapture={onInputFocus}>
                    <InputField
                        id='registration-username'
                        type='text'
                        value={
                            registrationData.username
                        }
                        label={t('username')}
                        changeFn={(
                            val: string
                        ) => {
                            updateRegistrationData(
                                'username',
                                val
                            )
                        }}
                        validate='letters/numbers'
                        validateEmptyOnBlur={false}
                        validationCheck={(
                            val: boolean
                        ) => {
                            setRegistrationLocked(
                                val
                            )
                        }}
                        validationErrorMessage={t(
                            'error:errorUsername'
                        )}
                        validationCharactersNumber={
                            4
                        }
                        additionalProps={{
                            placeholder:
                                t('username')
                        }}
                    />
                </div>

                <div
                    onFocusCapture={
                        onInputFocus
                    }
                >
                    <InputField
                        id='registration-email'
                        type='email'
                        value={
                            registrationData.email
                        }
                        label={t('email')}
                        changeFn={(
                            val: string
                        ) => {
                            updateRegistrationData(
                                'email',
                                val
                            )
                        }}
                        validate='email'
                        validateEmptyOnBlur={false}
                        validationCheck={(
                            val: boolean
                        ) => {
                            setRegistrationLocked(
                                val
                            )
                        }}
                        validationErrorMessage={t(
                            'error:errorEmail'
                        )}
                        additionalProps={{
                            placeholder:
                                t('email')
                        }}
                    />
                </div>

                <div
                    onFocusCapture={
                        onInputFocus
                    }
                >
                    <InputField
                        id='registration-password'
                        type={
                            passwordInputType
                                ? 'password'
                                : 'text'
                        }
                        value={
                            registrationData.password
                        }
                        label={t('password')}
                        changeFn={(
                            val: string
                        ) => {
                            updateRegistrationData(
                                'password',
                                val
                            )
                        }}
                        inputvisible={
                            passwordInputType
                        }
                        validateEmptyOnBlur={false}
                        swaptypeicon={true}
                        swaptype={() => {
                            setPasswordInputType(
                                !passwordInputType
                            )
                        }}
                        validate='password'
                        validationCheck={(
                            val: boolean
                        ) => {
                            setRegistrationLocked(
                                val
                            )
                        }}
                        validationErrorMessage={t(
                            'error:errorPassword'
                        )}
                        validationCharactersNumber={
                            8
                        }
                        additionalProps={{
                            placeholder:
                                t('password'),
                            autoComplete:
                                'new-password'
                        }}
                    />
                </div>

                <CheckboxField
                    customClass='auth-register__checkbox'
                    id='registration-terms'
                    value='terms'
                    label={
                        <>
                            {t(
                                'agreeToTerms'
                            )}{' '}
                            <a
                                href='/terms'
                                onClick={e => {
                                    e.preventDefault()
                                    setActiveLegalModal('terms')
                                }}
                            >
                                {t('terms')}
                            </a>
                            ,{' '}
                            <a
                                href='/privacypolicy'
                                onClick={e => {
                                    e.preventDefault()
                                    setActiveLegalModal('privacy')
                                }}
                            >
                                {t('privacyPolicy')}
                            </a>{' '}
                            {t('and')}{' '}
                            {t(
                                '18orolder'
                            )}
                        </>
                    }
                    checked={
                        termsAccepted
                    }
                    changeFn={() =>
                        setTermsAccepted(
                            !termsAccepted
                        )
                    }
                />

                <div className='auth-register__action auth-register__action--split'>
<button
  type='button'
  className={`auth-register__back-btn ${
    fromSubscribeModal
      ? 'auth-register__back-btn--subscribe-modal'
      : ''
  }`}
  onClick={() => {
    if (fromSubscribeModal) {
      onBackToLogin?.()
      return
    }

    handleBack()
  }}
>
  Back
</button>

                    <Button
                        text={
                            !registrationOpen &&
                            selectedRole ===
                                'creator'
                                ? 'Continue'
                                : registrationOpen
                                  ? t(
                                        'signup'
                                    )
                                  : 'Pre Register'
                        }
                        color='blue'
                        customClass='submit-btn auth-register__submit-btn'
                        clickFn={
                            handleAccountContinue
                        }
                        disabled={
                            registrationLocked ||
                            missingData() ||
                            isSubmitting
                        }
                    />
                </div>
            </div>
            {activeLegalModal === 'terms' && (
                <TermsModal
                    onClose={() => setActiveLegalModal(null)}
                />
            )}

            {activeLegalModal === 'privacy' && (
                <PrivacyPolicyModal
                    onClose={() => setActiveLegalModal(null)}
                />
            )}
        </div>
    )
}